import { computed, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import { fetchCurrentUser, login as requestLogin } from '@/services/authService'
import { ApiError } from '@/services/http'
import type { AuthUser, LoginPayload } from '@/types/auth'

/** Where the token is kept. Namespaced, because every app served from this origin shares the storage. */
const TOKEN_KEY = 'dropway.auth.token'

/** `setTimeout` cannot wait longer than this (2^31 - 1 ms, about 24.8 days). */
const MAX_TIMER_MS = 2_147_483_647

function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function writeStoredToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY)
    else window.localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // Storage can be blocked or full. The session then lasts until the tab is closed.
  }
}

/**
 * The `exp` claim of a JWT, in epoch milliseconds, or null when the token cannot be read. Nothing is
 * verified here, only the API can do that: this just keeps the app from sending a token it knows is dead.
 */
function tokenExpiry(token: string): number | null {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const claims: unknown = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')))
    const exp = typeof claims === 'object' && claims !== null ? (claims as { exp?: unknown }).exp : null
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null
  } catch {
    return null
  }
}

const isLive = (token: string): boolean => (tokenExpiry(token) ?? 0) > Date.now()

/**
 * Who is logged in. The JWT is kept in `localStorage`, so a reload or a new tab stays logged in, and
 * every tab follows a login or logout done in another. The account (`user`) is not stored: it is read
 * from the API again whenever a stored token is picked up.
 */
export const useAuthStore = defineStore('auth', () => {
  const tokenRef = shallowRef<string | null>(null)
  const userRef = shallowRef<AuthUser | null>(null)
  const readingAccount = shallowRef(false)
  let expiryTimer: ReturnType<typeof setTimeout> | undefined

  /** The JWT of the logged-in user, for the services that call the API on their behalf. */
  const token = computed(() => tokenRef.value)
  /** The logged-in user. Null while the account is still being loaded after a page load. */
  const user = computed(() => userRef.value)
  const isAuthenticated = computed(() => tokenRef.value !== null)
  /** The user holds the admin role. */
  const isAdmin = computed(() => userRef.value?.roles.includes('admin') ?? false)
  const permissions = computed(() => new Set(userRef.value?.permissions ?? []))

  /**
   * Whether the user may do `action` on `resource`, e.g. `can('service', 'create')`, by the
   * permissions the API listed. It only decides what a screen shows; the API decides the rest.
   */
  function can(resource: string, action: string): boolean {
    return permissions.value.has(`${resource}:${action}`)
  }
  /**
   * A token is held, its account is not known and nothing is reading it: the API could not be
   * reached when it was asked. `restore` asks again.
   */
  const accountFailed = computed(
    () => tokenRef.value !== null && userRef.value === null && !readingAccount.value,
  )

  /** Makes `value` the current token, or logs out when it is null, and arms the expiry timer. */
  function adopt(value: string | null, persist: boolean): void {
    tokenRef.value = value
    if (value === null) {
      userRef.value = null
      readingAccount.value = false
    }
    if (persist) writeStoredToken(value)

    clearTimeout(expiryTimer)
    if (value === null) return
    const remaining = (tokenExpiry(value) ?? 0) - Date.now()
    if (remaining <= 0) {
      adopt(null, persist)
      return
    }
    expiryTimer = setTimeout(() => {
      if (checkSession()) adopt(value, false)
    }, Math.min(remaining, MAX_TIMER_MS))
  }

  /** Whether a usable token is held. A token that ran out in the meantime is dropped on the spot. */
  function checkSession(): boolean {
    if (tokenRef.value === null) return false
    if (isLive(tokenRef.value)) return true
    adopt(null, true)
    return false
  }

  /**
   * Logs in. Rejects with an `ApiError` when the credentials are wrong or the API cannot be reached; the
   * state is left as it was.
   */
  async function login(payload: LoginPayload): Promise<void> {
    const session = await requestLogin(payload)
    if (!isLive(session.token)) {
      // The token is dead on arrival, which only happens when this device's clock is far off.
      throw new ApiError(0, 'clock_skew', 'The clock of this device does not match the server.')
    }
    userRef.value = session.user
    adopt(session.token, true)
  }

  function logout(): void {
    adopt(null, true)
  }

  /**
   * Loads the account behind the held token. A token the API refuses means the session is over; an API
   * that cannot be reached does not, so being offline never logs anyone out.
   */
  async function restore(): Promise<void> {
    const current = tokenRef.value
    if (current === null) return
    readingAccount.value = true
    try {
      const account = await fetchCurrentUser(current)
      if (tokenRef.value === current) userRef.value = account
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && tokenRef.value === current) logout()
    } finally {
      // Another token has its own read by now, and that one owns the flag.
      if (tokenRef.value === current) readingAccount.value = false
    }
  }

  /** Follows a change made in another tab. */
  function syncFromStorage(): void {
    const stored = readStoredToken()
    if (stored === tokenRef.value) return
    // The other tab may have logged in as someone else; do not show the previous account meanwhile.
    userRef.value = null
    adopt(stored !== null && isLive(stored) ? stored : null, false)
    void restore()
  }

  const initial = readStoredToken()
  if (initial !== null && isLive(initial)) {
    adopt(initial, false)
    void restore()
  } else if (initial !== null) {
    writeStoredToken(null)
  }

  window.addEventListener('storage', (event) => {
    // A null key means the whole storage was cleared.
    if (event.key === TOKEN_KEY || event.key === null) syncFromStorage()
  })

  return {
    token,
    user,
    isAuthenticated,
    isAdmin,
    accountFailed,
    can,
    login,
    logout,
    restore,
    checkSession,
  }
})
